import React, { useState } from 'react';
import { X, Plus, Trash2, Type as TypeIcon, UserCheck } from 'lucide-react';
import { roadmapApi } from '../../../lib/api';

interface ColumnDraft { header: string; type: 'text' | 'assign'; }

interface Props {
    sheetId: string;
    onClose: () => void;
    onCreated: () => void;
}

// Tableaux à colonnes personnalisables (demande d'Emeni) : plus de sélection de postes existants pour
// construire le titre — juste un titre libre, puis on choisit le nombre de colonnes et, pour chacune,
// son intitulé et son type (texte libre ou assignation d'un salarié). Au moins une colonne "Assigner
// un salarié" est requise (sinon rien n'est assignable dans ce tableau).
export const AddRoadmapTableModal: React.FC<Props> = ({ sheetId, onClose, onCreated }) => {
    const [title, setTitle] = useState('');
    const [columns, setColumns] = useState<ColumnDraft[]>([
        { header: 'Rang', type: 'text' },
        { header: 'Salarié assigné', type: 'assign' },
    ]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const addColumn = () => setColumns(prev => [...prev, { header: `Colonne ${prev.length + 1}`, type: 'text' }]);
    const removeColumn = (i: number) => setColumns(prev => prev.filter((_, idx) => idx !== i));
    const updateColumn = (i: number, patch: Partial<ColumnDraft>) => setColumns(prev => prev.map((col, idx) => idx === i ? { ...col, ...patch } : col));

    const handleSubmit = async () => {
        if (!title.trim()) { setError('Le titre du tableau est requis.'); return; }
        if (!columns.length) { setError('Ajoutez au moins une colonne.'); return; }
        if (!columns.some(c => c.type === 'assign')) { setError('Au moins une colonne doit être de type "Assigner un salarié".'); return; }
        setSaving(true);
        setError(null);
        try {
            const res = await roadmapApi.createTable(sheetId, title.trim(), columns.map(c => ({ header: c.header.trim() || 'Colonne', type: c.type })));
            if (res?.error) {
                setError(res.error);
                setSaving(false);
                return;
            }
            onCreated();
        } catch (e) {
            setError('Erreur lors de la création du tableau.');
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-[var(--bg-card)] rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-border shrink-0">
                    <h2 className="font-black text-slate-800 dark:text-content">Ajouter un tableau</h2>
                    <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-content"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-4 overflow-y-auto">
                    <div>
                        <label className="block text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2">Titre du tableau</label>
                        <input
                            type="text"
                            autoFocus
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Ex: Équipe Terrasse"
                            className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-border bg-white dark:bg-transparent text-slate-800 dark:text-content"
                        />
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Colonnes ({columns.length})</label>
                            <button type="button" onClick={addColumn} className="flex items-center gap-1 text-xs font-bold text-brand-500 hover:underline">
                                <Plus size={13} /> Ajouter une colonne
                            </button>
                        </div>
                        <div className="space-y-2">
                            {columns.map((col, i) => (
                                <div key={i} className="flex items-center gap-2 p-2 rounded-xl border border-slate-200 dark:border-border">
                                    <input
                                        type="text"
                                        value={col.header}
                                        onChange={(e) => updateColumn(i, { header: e.target.value })}
                                        placeholder={`Colonne ${i + 1}`}
                                        className="flex-1 min-w-0 px-2 py-1.5 rounded-lg border border-slate-200 dark:border-border bg-slate-50 dark:bg-[var(--bg-soft)] text-sm text-slate-800 dark:text-content"
                                    />
                                    <div className="flex bg-slate-100 dark:bg-[var(--bg-soft)] p-0.5 rounded-lg shrink-0">
                                        <button
                                            type="button"
                                            onClick={() => updateColumn(i, { type: 'text' })}
                                            title="Texte libre (saisie manuelle)"
                                            className={`p-1.5 rounded-md ${col.type === 'text' ? 'bg-white dark:bg-[var(--bg-card)] shadow-sm text-brand-500' : 'text-slate-400'}`}
                                        >
                                            <TypeIcon size={14} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => updateColumn(i, { type: 'assign' })}
                                            title="Assigner un salarié"
                                            className={`p-1.5 rounded-md ${col.type === 'assign' ? 'bg-white dark:bg-[var(--bg-card)] shadow-sm text-brand-500' : 'text-slate-400'}`}
                                        >
                                            <UserCheck size={14} />
                                        </button>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeColumn(i)}
                                        disabled={columns.length <= 1}
                                        className="p-1.5 text-slate-300 hover:text-red-500 disabled:opacity-30 shrink-0"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <p className="text-[11px] text-slate-400 mt-2">
                            <TypeIcon size={11} className="inline -mt-0.5" /> Texte libre = saisie manuelle. <UserCheck size={11} className="inline -mt-0.5" /> Assigner un salarié = liste déroulante des salariés prévus ce jour-là.
                        </p>
                    </div>
                    {error && <p className="text-sm text-red-500">{error}</p>}
                </div>
                <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-border shrink-0">
                    <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 dark:text-content hover:bg-slate-50 dark:hover:bg-surface-soft">Annuler</button>
                    <button onClick={handleSubmit} disabled={saving} className="px-4 py-2 rounded-xl text-sm font-semibold bg-brand-600 text-brand-fg hover:brightness-95 disabled:opacity-50">
                        {saving ? 'Création...' : 'Créer le tableau'}
                    </button>
                </div>
            </div>
        </div>
    );
};
